/**
 * Business Edit Profile — full rich edit surface.
 *
 * Route: /food-pulse/business/:id/profile/edit
 *
 * Sections:
 *   Address Info · Business Info (name, category, sub-category, logo,
 *   images, about, notes, owner personal + biz phone + email)
 *   Social Media · Website / Loyalty / Coupon flyer / Product / Deals /
 *   Coupons page links · Consents · Feature toggles (store, parking,
 *   wheelchair, delivery, bags, payment) · Food-assistance cards
 *   accepted · Business hours.
 *
 * Uses PATCH /api/foodpulse/business/:id/profile which merges each
 * sub-object independently.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Facebook, Instagram, Twitter, Linkedin, Youtube, Globe, ArrowLeft, Save } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const PURPLE = "#7C3AED";

const CARDS = ["snap", "wic", "ebt", "sfmnp", "none", "other"];
const DOW = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function Card({ title, children, testid }) {
  return (
    <div className="rounded-2xl bg-white border p-5 mb-4" style={{ borderColor: "#E9D8B4" }} data-testid={testid}>
      <h2 className="text-lg font-bold mb-3" style={{ color: GREEN }}>{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="text-xs font-semibold text-gray-600 block">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      <button type="button" onClick={() => onChange(true)} className={`px-3 py-1 rounded-full text-xs font-semibold border ${value === true ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={value !== true ? { borderColor: GREEN, color: GREEN } : {}}>Yes</button>
      <button type="button" onClick={() => onChange(false)} className={`px-3 py-1 rounded-full text-xs font-semibold border ${value === false ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={value !== false ? { borderColor: GREEN, color: GREEN } : {}}>No</button>
    </div>
  );
}

function Chk({ checked, onChange, label, testid }) {
  return <label className="flex items-center gap-1 text-sm" data-testid={testid}><input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#0F3D2E]" />{label}</label>;
}

async function toDataURL(file) {
  return await new Promise((res, rej) => {
    const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file);
  });
}

export default function FoodPulseBusinessEdit() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [biz, setBiz] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Sub-forms
  const [address, setAddress] = useState({});
  const [info, setInfo] = useState({});
  const [social, setSocial] = useState({});
  const [links, setLinks] = useState({});
  const [consents, setConsents] = useState({});
  const [features, setFeatures] = useState({});
  const [hours, setHours] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await http.get(`/foodpulse/business/${id}/profile`);
      const b = r.data.business;
      setBiz(b);
      setAddress(b.fp_business_address || { country: b.country, state: b.state, city: b.city, zip: b.zip, line1: b.address, lat: b.lat, lon: b.lon });
      setInfo(b.fp_business_info || { name: b.name, category: b.category, email: b.email });
      setSocial(b.fp_business_social || {});
      setLinks(b.fp_business_links || { website: b.website });
      setConsents(b.fp_business_consents || { notification_consent: true, bulk_surplus_interest: true, connect_for_deals_offers: true });
      setFeatures(b.fp_business_features || { accepts_food_assistance: [] });
      setHours(b.fp_business_hours || {});
    } catch (e) { setMsg(e.response?.data?.detail || "Load failed"); }
  }, [id]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await http.patch(`/foodpulse/business/${id}/profile`, { address, info, social, links, consents, features, hours });
      setMsg("Saved!");
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { alert("Max 3 MB"); return; }
    setInfo({ ...info, logo_url: await toDataURL(f) });
  };
  const addImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { alert("Max 3 MB"); return; }
    setInfo({ ...info, images: [...(info.images || []), await toDataURL(f)] });
  };
  const toggleCard = (k) => setFeatures({ ...features, accepts_food_assistance: (features.accepts_food_assistance || []).includes(k) ? features.accepts_food_assistance.filter((x) => x !== k) : [...(features.accepts_food_assistance || []), k] });

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!user) return <div className="p-10 text-center">Please sign in.</div>;
  if (!biz) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <main className="min-h-screen py-8" style={{ background: CREAM }} data-testid="business-edit-page">
      <div className="max-w-3xl mx-auto px-4">
        <Link to={`/food-pulse/businesses/${biz.slug}`} className="inline-flex items-center gap-1 text-sm font-semibold mb-3" style={{ color: GREEN }}><ArrowLeft size={14} />Back</Link>
        <h1 className="text-2xl font-black text-center mb-6" style={{ color: PURPLE }}>Edit Business Profile</h1>

        <Card title="Address Info" testid="card-address">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Country" value={address.country || ""} onChange={(e) => setAddress({ ...address, country: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="addr-country" />
            <input placeholder="State" value={address.state || ""} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <input placeholder="City" value={address.city || ""} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <input placeholder="ZIP" value={address.zip || ""} onChange={(e) => setAddress({ ...address, zip: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <input placeholder="Address line 1" value={address.line1 || ""} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="col-span-2 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <input placeholder="Apt / Suite" value={address.apt || ""} onChange={(e) => setAddress({ ...address, apt: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <input placeholder="Lat, Lon (auto or manual)" value={address.lat && address.lon ? `${address.lat}, ${address.lon}` : ""} onChange={(e) => { const [la, lo] = e.target.value.split(",").map((s) => parseFloat(s.trim())); setAddress({ ...address, lat: la || undefined, lon: lo || undefined }); }} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          </div>
        </Card>

        <Card title="Business Info" testid="card-info">
          <div className="grid gap-2">
            <input placeholder="Business name" value={info.name || ""} onChange={(e) => setInfo({ ...info, name: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="info-name" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Category" value={info.category || ""} onChange={(e) => setInfo({ ...info, category: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
              <input placeholder="Sub-category" value={info.sub_category || ""} onChange={(e) => setInfo({ ...info, sub_category: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            </div>
            <Field label="Logo">
              <div className="flex items-center gap-2">
                {info.logo_url && <img src={info.logo_url} alt="" className="w-16 h-16 rounded-lg object-cover border" style={{ borderColor: "#E9D8B4" }} />}
                <label className="border rounded-lg px-3 py-2 text-xs cursor-pointer" style={{ borderColor: "#E9D8B4" }}>Upload logo<input type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="upload-logo-input" /></label>
              </div>
            </Field>
            <Field label="Images">
              <div className="flex flex-wrap gap-2 items-center">
                {(info.images || []).map((src, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border" style={{ borderColor: "#E9D8B4" }}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setInfo({ ...info, images: info.images.filter((_, x) => x !== i) })} className="absolute top-0 right-0 bg-red-600 text-white text-[10px] w-4 h-4 rounded-bl">×</button>
                  </div>
                ))}
                <label className="border rounded-lg px-3 py-2 text-xs cursor-pointer" style={{ borderColor: "#E9D8B4" }}>+ Add image<input type="file" accept="image/*" hidden onChange={addImage} data-testid="add-image-input" /></label>
              </div>
            </Field>
            <textarea rows="2" placeholder="About business" value={info.about || ""} onChange={(e) => setInfo({ ...info, about: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="info-about" />
            <textarea rows="2" placeholder="Special notes (dietary options, delivery zones, etc.)" value={info.special_notes || ""} onChange={(e) => setInfo({ ...info, special_notes: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Owner first name" value={info.owner_first_name || ""} onChange={(e) => setInfo({ ...info, owner_first_name: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
              <input placeholder="Owner last name" value={info.owner_last_name || ""} onChange={(e) => setInfo({ ...info, owner_last_name: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
              <select value={info.owner_gender || ""} onChange={(e) => setInfo({ ...info, owner_gender: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }}>
                <option value="">Owner gender</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
              </select>
              <input placeholder="Owner email" value={info.email || ""} onChange={(e) => setInfo({ ...info, email: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            </div>
            <div className="flex gap-2"><input placeholder="+1" value={info.personal_phone_cc || ""} onChange={(e) => setInfo({ ...info, personal_phone_cc: e.target.value })} className="w-16 border rounded-lg px-2 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} /><input placeholder="Owner personal phone" value={info.personal_phone || ""} onChange={(e) => setInfo({ ...info, personal_phone: e.target.value })} className="flex-1 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} /></div>
            <div className="flex gap-2"><input placeholder="+1" value={info.business_phone_cc || ""} onChange={(e) => setInfo({ ...info, business_phone_cc: e.target.value })} className="w-16 border rounded-lg px-2 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} /><input placeholder="Business phone" value={info.business_phone || ""} onChange={(e) => setInfo({ ...info, business_phone: e.target.value })} className="flex-1 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} /></div>
          </div>
        </Card>

        <Card title="Social Media" testid="card-social">
          {[["facebook", Facebook], ["instagram", Instagram], ["twitter", Twitter], ["linkedin", Linkedin], ["tiktok", Globe], ["youtube", Youtube]].map(([k, Icon]) => (
            <div key={k} className="flex items-center gap-2 mb-2">
              <Icon size={16} style={{ color: GREEN }} />
              <input value={social[k] || ""} onChange={(e) => setSocial({ ...social, [k]: e.target.value })} placeholder={`${k[0].toUpperCase()}${k.slice(1)} URL`} className="flex-1 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid={`social-${k}`} />
            </div>
          ))}
        </Card>

        <Card title="Links" testid="card-links">
          {[["website", "Business / Charity Website"], ["loyalty_program_link", "Loyalty Program Link"], ["weekly_coupons_flyer_link", "Weekly Coupons Flyer Link"], ["product_page_link", "Product Page Link"], ["deals_page_link", "Deals Page Link"], ["coupons_page_link", "Coupons Page Link"]].map(([k, label]) => (
            <input key={k} value={links[k] || ""} onChange={(e) => setLinks({ ...links, [k]: e.target.value })} placeholder={label} className="w-full border rounded-lg px-3 py-2 text-sm mb-2" style={{ borderColor: "#E9D8B4" }} data-testid={`links-${k}`} />
          ))}
        </Card>

        <Card title="Consents" testid="card-consents">
          <Chk checked={consents.notification_consent} onChange={(v) => setConsents({ ...consents, notification_consent: v })} label="Notification consent to receive phone & email notifications" testid="cn-notif" />
          <Chk checked={consents.bulk_surplus_interest} onChange={(v) => setConsents({ ...consents, bulk_surplus_interest: v })} label="Bulk (Surplus) Food Interest" testid="cn-bulk" />
          <Chk checked={consents.connect_for_deals_offers} onChange={(v) => setConsents({ ...consents, connect_for_deals_offers: v })} label="Connect with consumers to send Deals & Offers" testid="cn-deals" />
        </Card>

        <Card title="Amenities & Services" testid="card-features">
          <div className="grid gap-2">
            <Field label="Do you have a Store/Shop?"><YesNo value={features.has_store} onChange={(v) => setFeatures({ ...features, has_store: v })} testid="feat-store" /></Field>
            <Field label="Parking on premises?"><YesNo value={features.parking_on_premises} onChange={(v) => setFeatures({ ...features, parking_on_premises: v })} testid="feat-parking" /></Field>
            <Field label="Wheelchair access?"><YesNo value={features.wheelchair_access} onChange={(v) => setFeatures({ ...features, wheelchair_access: v })} testid="feat-wheelchair" /></Field>
            <Field label="Delivery service?"><YesNo value={features.delivery_service} onChange={(v) => setFeatures({ ...features, delivery_service: v })} testid="feat-delivery" /></Field>
            <Field label="Shopping bags?"><YesNo value={features.shopping_bags} onChange={(v) => setFeatures({ ...features, shopping_bags: v })} testid="feat-bags" /></Field>
            {features.shopping_bags && (
              <div className="pl-4 flex flex-wrap gap-3">
                <label className="text-xs flex items-center gap-1"><input type="radio" name="bagmode" checked={features.shopping_bags_mode === "free"} onChange={() => setFeatures({ ...features, shopping_bags_mode: "free" })} />Free</label>
                <label className="text-xs flex items-center gap-1"><input type="radio" name="bagmode" checked={features.shopping_bags_mode === "paid"} onChange={() => setFeatures({ ...features, shopping_bags_mode: "paid" })} />Paid</label>
                <Chk checked={features.free_bag} onChange={(v) => setFeatures({ ...features, free_bag: v })} label="Free bag" testid="feat-free-bag" />
                <Chk checked={features.free_cartons} onChange={(v) => setFeatures({ ...features, free_cartons: v })} label="Free cartons" testid="feat-free-carton" />
              </div>
            )}
            <Field label="Payment options available?"><YesNo value={features.payment_options_available} onChange={(v) => setFeatures({ ...features, payment_options_available: v })} testid="feat-payment" /></Field>
            <Field label="Accept Food Assistance Cards?">
              <div className="grid grid-cols-3 gap-1">
                {CARDS.map((c) => <Chk key={c} checked={(features.accepts_food_assistance || []).includes(c)} onChange={() => toggleCard(c)} label={c.toUpperCase()} testid={`feat-card-${c}`} />)}
              </div>
            </Field>
          </div>
        </Card>

        <Card title="Business Hours" testid="card-hours">
          <div className="grid gap-1">
            {DOW.map((d) => {
              const day = hours[d] || {};
              return (
                <div key={d} className="grid grid-cols-4 gap-2 items-center text-xs">
                  <span className="capitalize font-semibold" style={{ color: GREEN }}>{d}</span>
                  <input type="time" value={day.open || ""} onChange={(e) => setHours({ ...hours, [d]: { ...day, open: e.target.value } })} disabled={day.closed} className="border rounded px-2 py-1" style={{ borderColor: "#E9D8B4" }} data-testid={`hrs-${d}-open`} />
                  <input type="time" value={day.close || ""} onChange={(e) => setHours({ ...hours, [d]: { ...day, close: e.target.value } })} disabled={day.closed} className="border rounded px-2 py-1" style={{ borderColor: "#E9D8B4" }} />
                  <label className="flex items-center gap-1"><input type="checkbox" checked={!!day.closed} onChange={(e) => setHours({ ...hours, [d]: { ...day, closed: e.target.checked } })} />Closed</label>
                </div>
              );
            })}
          </div>
        </Card>

        {msg && <div className="text-sm text-center text-gray-700 mb-3" data-testid="biz-save-msg">{msg}</div>}

        <button onClick={save} disabled={busy} className="w-full rounded-full py-3 font-semibold text-sm sticky bottom-3 disabled:opacity-60" style={{ background: PURPLE, color: "white" }} data-testid="biz-save-btn">
          {busy ? <Loader2 className="animate-spin inline" size={14} /> : <><Save size={14} className="inline mr-1" />Update Profile</>}
        </button>
      </div>
    </main>
  );
}
